import { z } from 'zod';

/**
 * The analytics boundary (TZ §5). Everything append-only and enormous lives in ClickHouse
 * behind this port; PostgreSQL never learns about it. Unit tests mock the port, so the
 * ClickHouse container is needed only by its own integration suite and by E2E.
 */

export const gscMetricRowSchema = z.object({
  siteId: z.uuid(),
  date: z.string(), // YYYY-MM-DD
  page: z.string(),
  query: z.string(),
  country: z.string(),
  device: z.string(),
  clicks: z.number().int().nonnegative(),
  impressions: z.number().int().nonnegative(),
  ctr: z.number(),
  position: z.number(),
});
export type GscMetricRow = z.infer<typeof gscMetricRowSchema>;

export const uptimeCheckRowSchema = z.object({
  siteId: z.uuid(),
  checkedAt: z.date(),
  httpStatus: z.number().int(),
  responseMs: z.number().int().nonnegative(),
  blocksOk: z.boolean(),
  error: z.string().default(''),
});
export type UptimeCheckRow = z.infer<typeof uptimeCheckRowSchema>;

export interface DateRange {
  from: string;
  to: string;
}

/**
 * Which sites a network-wide aggregate covers.
 *
 * `'all'` is not sugar for "every id": the dashboard used to enumerate 5 214 uuids and hand
 * them to ClickHouse as an `IN` list on every request, which is a different query plan and
 * an order of magnitude slower — under load it stopped serving entirely. `'all'` drops the
 * predicate. An explicit (possibly empty) list still means exactly those sites, and an empty
 * one still means none, which is what a geo-scoped actor with no markets must see.
 */
export type SiteSelector = string[] | 'all';

export interface GscSummary {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface GscSeriesPoint {
  date: string;
  clicks: number;
  impressions: number;
  position: number;
}

export interface GscQueryRow {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface GscPageRow {
  page: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

/** §6 hint families. Produced by SQL, never by an LLM. */
export interface GscInsights {
  /** Position 11–20: one push away from page one. */
  striking: GscQueryRow[];
  /** Two or more pages ranking for the same query. */
  cannibalization: { query: string; pages: { page: string; clicks: number }[] }[];
  /** Impressions but no landing page bound in the core. */
  withoutPage: GscQueryRow[];
  /** Good position, poor click-through — a title/description problem. */
  lowCtr: GscQueryRow[];
}

/**
 * Розріз метрик по одному вимірі — пристрій або країна.
 *
 * `gsc_metrics` тримає `country` і `device` колонками з першого дня, і жоден запит їх не
 * читав: картка сайту показувала сумарні кліки, у яких мобільний провал і десктопне зростання
 * гасять одне одного до рівної лінії. Саме цей розріз і відрізняє «трафік стабільний» від
 * «половина аудиторії пішла».
 */
export interface GscSplitRow {
  /** `DESKTOP`/`MOBILE`/`TABLET` або тризначний код країни, як їх віддає Search Console. */
  key: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface UptimeSummary {
  siteId: string;
  date: string;
  checks: number;
  failures: number;
  avgResponseMs: number;
}

export interface AnalyticsStore {
  insertGscMetrics(rows: GscMetricRow[]): Promise<void>;
  insertUptimeChecks(rows: UptimeCheckRow[]): Promise<void>;

  gscSummary(sites: SiteSelector, range: DateRange): Promise<GscSummary>;
  gscSeries(sites: SiteSelector, range: DateRange): Promise<GscSeriesPoint[]>;
  gscTopQueries(siteId: string, range: DateRange, limit: number): Promise<GscQueryRow[]>;
  gscTopPages(siteId: string, range: DateRange, limit: number): Promise<GscPageRow[]>;

  /**
   * `knownKeywords` is the site's core; queries outside it drive the "not in core" hint
   * and the `withoutPage` list.
   */
  gscInsights(
    siteId: string,
    range: DateRange,
    knownKeywords: string[],
    limit: number,
  ): Promise<GscInsights>;

  gscCompare(
    sites: SiteSelector,
    current: DateRange,
    previous: DateRange,
  ): Promise<{ current: GscSummary; previous: GscSummary }>;

  /** Розподіл по пристроях за період — усі значення, їх одиниці. */
  gscByDevice(siteId: string, range: DateRange): Promise<GscSplitRow[]>;

  /** Найбільші країни за кліками; решта відсікається лімітом, а не ховається. */
  gscByCountry(siteId: string, range: DateRange, limit: number): Promise<GscSplitRow[]>;

  uptimeDaily(siteIds: string[], range: DateRange): Promise<UptimeSummary[]>;
  lastUptime(siteIds: string[]): Promise<Map<string, { httpStatus: number; checkedAt: Date }>>;

  /** Total clicks over the range — the "traffic at risk" aggregate for bulk ops (§5.4). */
  trafficAtRisk(siteIds: string[], range: DateRange): Promise<Map<string, number>>;

  healthy(): Promise<boolean>;

  /**
   * Tables that should carry a retention TTL and do not (§11).
   *
   * A ping proves the server answers; it says nothing about whether the TTL a migration was
   * supposed to create still exists — and a dropped TTL is invisible until the disk fills.
   */
  tablesWithoutRetention(): Promise<string[]>;
}
